// "Watch it converge" player (FXC-9009): replays a V100 RANS solve's intermediate frames — frame 0 is
// the NN warm-start guess, frames refine to the converged field — through the same LIC FlowVisLayer the
// workbench uses, alongside the live CL-vs-step trace. Fed by a bundle written by nn_local/solve_and_bundle.py
// (sliceOutput.animationFrequencyInPseudoStepping frames → one shared-grid JSON).
import { useEffect, useMemo, useState } from "react";
import { FlowVisLayer, type FlowMesh } from "./foil/FlowVisLayer";
import { useHighliftTheme } from "./foil/useHighliftTheme";
import type { ViewRect } from "./geometry";

interface ConvFrame {
  step: number;
  CL: number | null;
  CD: number | null;
  cp: number[];
  mach: number[];
  vel: number[];
  machRange: [number, number];
}
interface ConvBundle {
  meta: { alpha: number; mach: number; re: number; blow: number; grid: [number, number]; clip: [number, number, number, number]; n_frames: number; warm_start?: boolean };
  grid: { points: number[]; tris: number[] };
  frames: ConvFrame[];
  cl_history: { step: number; CL: number; CD: number }[];
}

const mono: React.CSSProperties = { font: "600 11px ui-monospace, SFMono-Regular, monospace" };

export function ConvergencePlayer({
  width,
  height,
  bundleUrl = "/convergence_frames.json",
}: {
  width: number;
  height: number;
  bundleUrl?: string;
}) {
  const palette = useHighliftTheme();
  // Themed HUD chrome — a translucent panel over the LIC field that reads in both light + dark.
  const hud = useMemo(() => ({
    background: `color-mix(in srgb, ${palette.surface} 82%, transparent)`,
    border: `1px solid ${palette.line}`,
  }), [palette]);
  const [bundle, setBundle] = useState<ConvBundle | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    let on = true;
    fetch(bundleUrl)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((b: ConvBundle) => { if (on) { setBundle(b); setIdx(0); } })
      .catch((e: unknown) => { if (on) setErr(e instanceof Error ? e.message : "load failed"); });
    return () => { on = false; };
  }, [bundleUrl]);

  const nFrames = bundle ? bundle.frames.length : 0;
  useEffect(() => {
    if (!playing || nFrames === 0) return;
    const t = window.setInterval(() => setIdx((i) => (i + 1) % nFrames), 650);
    return () => window.clearInterval(t);
  }, [playing, nFrames]);

  const view: ViewRect = useMemo(() => {
    const c = bundle?.meta.clip ?? [-0.7, 1.7, -0.78, 0.62];
    return { xmin: c[0]!, xmax: c[1]!, ymin: c[2]!, ymax: c[3]! };
  }, [bundle]);

  const frame = bundle?.frames[idx] ?? null;
  const mesh: FlowMesh | null = useMemo(() => {
    if (!bundle || !frame) return null;
    return { points: bundle.grid.points, tris: bundle.grid.tris, vel: frame.vel, mach: frame.mach, machRange: frame.machRange };
  }, [bundle, frame]);

  if (err) return <div style={{ ...mono, color: palette.warn, padding: 8 }}>convergence bundle unavailable ({err})</div>;
  if (!bundle || !frame) return <div style={{ ...mono, color: palette.ink3, padding: 8 }}>loading convergence…</div>;

  const isFirst = idx === 0;
  const isLast = idx === nFrames - 1;
  const clh = bundle.cl_history;
  const steps = clh.map((p) => p.step);
  const cls = clh.map((p) => p.CL);
  const smin = Math.min(...steps), smax = Math.max(...steps);
  const cmin = Math.min(...cls), cmax = Math.max(...cls);
  const CW = 188, CH = 60, pad = 5;
  const sx = (s: number) => pad + (CW - 2 * pad) * (s - smin) / Math.max(1, smax - smin);
  const sy = (c: number) => CH - pad - (CH - 2 * pad) * (c - cmin) / Math.max(1e-6, cmax - cmin);
  const path = clh.map((p, i) => (i ? "L" : "M") + sx(p.step).toFixed(1) + " " + sy(p.CL).toFixed(1)).join(" ");

  return (
    <div style={{ position: "relative", width, height, background: palette.bg }}>
      <div style={{ position: "absolute", inset: 0 }}>
        <FlowVisLayer flow={mesh} width={width} height={height} view={view} />
      </div>
      {/* stage label + current CL */}
      <div style={{ position: "absolute", top: 8, left: 8, zIndex: 3, ...mono, color: palette.ink, ...hud, padding: "4px 9px", borderRadius: 6 }}>
        {isFirst ? "NN warm-start guess" : isLast ? "Converged (RANS)" : `solving · pseudo-step ${frame.step}`}
        {"  ·  CL "}{frame.CL != null ? frame.CL.toFixed(3) : "—"}
      </div>
      {/* CL-vs-step trace with marker */}
      <svg width={CW} height={CH} style={{ position: "absolute", top: 8, right: 8, zIndex: 3, ...hud, borderRadius: 6 }}>
        <path d={path} fill="none" stroke={palette.blue} strokeWidth={1.5} />
        <circle cx={sx(frame.step)} cy={sy(frame.CL ?? cmin)} r={3} fill={palette.ink} />
        <text x={pad} y={10} fill={palette.ink3} fontSize={8}>CL {cmin.toFixed(2)}→{cmax.toFixed(2)}</text>
      </svg>
      {/* transport */}
      <div style={{ position: "absolute", bottom: 8, left: 8, right: 8, zIndex: 3, display: "flex", gap: 8, alignItems: "center", ...hud, borderRadius: 6, padding: "5px 8px" }}>
        <button type="button" onClick={() => setPlaying((p) => !p)} style={{ ...mono, cursor: "pointer", color: palette.onPrimary, background: palette.primary, border: "none", borderRadius: 4, padding: "2px 9px" }}>
          {playing ? "⏸ pause" : "▶ play"}
        </button>
        <input type="range" min={0} max={nFrames - 1} value={idx} onChange={(e) => { setPlaying(false); setIdx(Number(e.target.value)); }} style={{ flex: 1, accentColor: palette.primary }} />
        <span style={{ ...mono, color: palette.ink }}>{idx + 1}/{nFrames}</span>
      </div>
    </div>
  );
}
